import { uniq } from "lodash";

import { INTERCOM_FAQ_URL } from "@olivahealth/constants";
import UserRole from "@olivahealth/business/src/Core/domain/value-objects/UserRole";
import {
  BriefCaseIcon,
  CalendarIcon,
  ChatBalloonIcon,
  CounterDot,
  GroupIcon,
  HomeIcon,
  NavbarLink,
  ReviewIcon,
  NavbarLinkType,
  PhoneIcon,
  PieChartIcon,
  QuestionCircleIcon,
  ResourcesIcon,
  UserIcon,
  UsersIcon,
} from "@olivahealth/oli-ui";
import {
  containsEmployeeRole,
  containsSupervisorRole,
} from "@olivahealth/utils/helpers/user";
import { Suspense } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { FeatureFlagsKeys } from "@olivahealth/utils/types/FeatureFlags";
import useHasOlivaAtWorkAccess from "../../../hooks/useHasOlivaAtWorkAccess";
import { OlivaHook } from "../../../hooks/OlivaHook";
import useTranslation, { TFunction } from "../../../hooks/useTranslation";
import { useSharedStoreContext } from "../../../services/contexts/SharedStoreContext";
import {
  IUserDataContext,
  useUserData,
} from "../../../services/contexts/UserDataContext";
import { PAGE_ROUTE } from "../../../utils/routing/routes";
import useCanAccessProfessionalCoaching from "../../../hooks/useCanAccessProfessionalCoaching";
import { useChatUnreadCount } from "../../../hooks/useChatUnreadCount";
import { getAdminLinks } from "../shared/adminLinks";

const LINK_ORGANISATION_TEAM_SESSIONS_ID = "navbar-link-team-sessions";
const LINK_PROFILE_ID = "navbar-link-profile";
const LINK_SUPPORT_ID = "navbar-link-1-to-1";

interface UseNavbarData {
  isOrganisationAdmin: boolean;
  isProfessional: boolean;
  links: NavbarLink[];
}

interface GetNavbarLinksProps {
  user: IUserDataContext["data"];
  isProfessional: boolean;
  canAccessProfessionalCoaching: boolean;
  crisisSupportEnabled: boolean;
  hasSurveyProfessionalNPSPendingSubmission: boolean;
  hasOlivaAtWorkAccess: boolean;
  googleDriveIntegrationEnabled: boolean;
  t: TFunction;
  tOlivaAtWork: TFunction;
}

type UseNavbar = OlivaHook<UseNavbarData>;

export default function useNavbar(): UseNavbar {
  const { data: user } = useUserData();
  const { t } = useTranslation("common", { keyPrefix: "navbar" });
  const { t: tOlivaAtWork } = useTranslation("OlivaAtWork");
  const sharedStoreContext = useSharedStoreContext();

  const { hasAccess: hasOlivaAtWorkAccess } = useHasOlivaAtWorkAccess();
  const isGoogleDriveIntegrationEnabled = useFeatureIsOn(
    `${FeatureFlagsKeys.GOOGLE_DRIVE_INTEGRATION}`,
  );

  const isEmployee = containsEmployeeRole(user?.role);
  const canAccessProfessionalCoaching = useCanAccessProfessionalCoaching();
  const isOrganisationAdmin =
    user?.role?.includes(UserRole.ORGANISATION_ADMIN) ?? false;
  const isProfessional = user?.role?.includes(UserRole.MHP) ?? false;
  const isClinicalSupervisor = containsSupervisorRole(user?.role);
  const isAdmin = user?.role?.includes(UserRole.ADMIN) ?? false;
  const isImpersonator = user?.role?.includes(UserRole.IMPERSONATOR) ?? false;
  const hasTeamSessionsOnly = user?.organisation?.hasTeamSessionsOnly ?? false;
  const crisisSupportEnabled =
    user?.organisation?.settings?.crisisSupportEnabled ?? false;
  const hasSurveyProfessionalNPSPendingSubmission =
    sharedStoreContext.surveyProfessionalNPS.pendingSubmissionId !== null;

  const {
    employeeLinks,
    organisationAdminLinks,
    professionalLinks,
    clinicalSupervisorLinks,
    impersonatorLinks,
    adminLinks,
  } = getNavbarLinks({
    user,
    isProfessional,
    canAccessProfessionalCoaching,
    crisisSupportEnabled,
    hasSurveyProfessionalNPSPendingSubmission,
    hasOlivaAtWorkAccess,
    googleDriveIntegrationEnabled: isGoogleDriveIntegrationEnabled,
    t,
    tOlivaAtWork,
  });

  let uniqueLinks = uniq([
    ...(isEmployee ? employeeLinks : []),
    ...(isOrganisationAdmin ? organisationAdminLinks : []),
    ...(isProfessional ? professionalLinks : []),
    ...(isClinicalSupervisor ? clinicalSupervisorLinks : []),
    ...(isAdmin ? adminLinks : []),
    ...(isImpersonator ? impersonatorLinks : []),
  ]);

  if (hasTeamSessionsOnly) {
    uniqueLinks = uniqueLinks.filter((link) =>
      [LINK_ORGANISATION_TEAM_SESSIONS_ID, LINK_PROFILE_ID].includes(
        link.id ?? "",
      ),
    );
  }

  return {
    status: "success",
    data: {
      isOrganisationAdmin,
      isProfessional,
      links: uniqueLinks,
    },
    effects: {},
  };
}

interface GetNavbarLinksData {
  clinicalSupervisorLinks: NavbarLink[];
  employeeLinks: NavbarLink[];
  professionalLinks: NavbarLink[];
  organisationAdminLinks: NavbarLink[];
  adminLinks: NavbarLink[];
  impersonatorLinks: NavbarLink[];
}

function buildHomeNavBarLink(
  t: TFunction,
  hasSurveyProfessionalNPSPendingSubmission: boolean,
) {
  return {
    activeRegex: "\\/$|/companion",
    href: "/",
    icon: <HomeIcon />,
    label: t("home"),
    role: Object.values(UserRole),
    type: NavbarLinkType.PRIMARY,
    id: "navbar-link-home",
    rightSideIcon: hasSurveyProfessionalNPSPendingSubmission && (
      <CounterDot count={1} />
    ),
  };
}

function buildMessagesNavBarLink(t: TFunction, isProfessional: boolean) {
  return {
    activeRegex: "/messages",
    href: "/messages",
    icon: <ChatBalloonIcon />,
    label: t("messages"),
    role: Object.values(UserRole),
    type: isProfessional ? NavbarLinkType.PRIMARY : NavbarLinkType.SECONDARY,
    rightSideIcon: <MessageCounterDot />,
  };
}

function buildProfessionalClientsNavBarLink(
  t: TFunction,
  isProfessional: boolean,
): NavbarLink | null {
  if (!isProfessional) {
    return null;
  }

  return {
    activeRegex: "/professionals/clients",
    href: "/professionals/clients",
    icon: <UsersIcon />,
    label: t("clients"),
    role: [UserRole.MHP],
    type: NavbarLinkType.PRIMARY,
  };
}

function buildCrisisSupportNavBarLink(
  t: TFunction,
  crisisSupportEnabled: boolean,
) {
  if (!crisisSupportEnabled) {
    return null;
  }

  return {
    activeRegex: "/crisis-support",
    href: "/crisis-support",
    icon: <PhoneIcon />,
    label: t("crisisSupport"),
    role: Object.values(UserRole),
    type: NavbarLinkType.SECONDARY,
    color: "tertiary",
  };
}

function buildOlivaAtWorkNavBarLink(
  hasOlivaAtWorkAccess: boolean,
  tOlivaAtWork: TFunction,
): NavbarLink | null {
  if (!hasOlivaAtWorkAccess) {
    return null;
  }

  return {
    activeRegex: "/work",
    href: "/work",
    icon: <BriefCaseIcon />,
    label: tOlivaAtWork("navbar.title"),
    role: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.ORGANISATION_ADMIN],
    type: NavbarLinkType.PRIMARY,
    id: "navbar-link-oliva-at-work",
  };
}

function getNavbarLinks({
  user,
  isProfessional,
  canAccessProfessionalCoaching,
  crisisSupportEnabled,
  hasSurveyProfessionalNPSPendingSubmission,
  hasOlivaAtWorkAccess,
  googleDriveIntegrationEnabled,
  t,
  tOlivaAtWork,
}: GetNavbarLinksProps): GetNavbarLinksData {
  /**
   * This list has all the possible links in the navbar (employee, professional and admins).
   */
  const links: NavbarLink[] = [
    buildHomeNavBarLink(t, hasSurveyProfessionalNPSPendingSubmission),
    {
      activeRegex: "/support",
      href: "/support",
      icon: <UsersIcon />,
      label: t("oneToOne"),
      role: [UserRole.EMPLOYEE, UserRole.ORGANISATION_ADMIN, UserRole.MANAGER],
      type: NavbarLinkType.PRIMARY,
      id: LINK_SUPPORT_ID,
    },
    ...(canAccessProfessionalCoaching
      ? [
          {
            activeRegex: "/professional-coaching",
            href: "/professional-coaching",
            icon: <BriefCaseIcon />,
            label: t("coaching"),
            role: [
              UserRole.EMPLOYEE,
              UserRole.ORGANISATION_ADMIN,
              UserRole.MANAGER,
            ],
            type: NavbarLinkType.PRIMARY,
            id: "navbar-link-coaching",
          },
        ]
      : []),
    {
      activeRegex: "/self-guided",
      href: "/self-guided",
      icon: <ResourcesIcon />,
      label: t("selfGuided"),
      role: [UserRole.EMPLOYEE, UserRole.ORGANISATION_ADMIN, UserRole.MANAGER],
      type: NavbarLinkType.PRIMARY,
      id: "navbar-link-self-guided",
    },
    buildOlivaAtWorkNavBarLink(hasOlivaAtWorkAccess, tOlivaAtWork),
    buildProfessionalClientsNavBarLink(t, isProfessional),
    {
      activeRegex: "/professionals/availability",
      href: "/professionals/availability",
      icon: <CalendarIcon />,
      label: t("calendar"),
      role: [UserRole.MHP],
      type: NavbarLinkType.PRIMARY,
    },
    {
      activeRegex: "/professionals/library|/resources/*",
      href: "/professionals/library",
      icon: <ResourcesIcon />,
      label: t("resources"),
      role: [UserRole.MHP],
      type: NavbarLinkType.PRIMARY,
      id: "navbar-link-resources",
    },
    {
      href: INTERCOM_FAQ_URL,
      isExternal: true,
      label: t("faqs"),
      icon: <QuestionCircleIcon />,
      role: [UserRole.MHP],
      type: NavbarLinkType.PRIMARY,
    },
    // Org Admin section Links
    {
      activeRegex: "/organisation/reports",
      href: "/organisation/reports",
      icon: <PieChartIcon />,
      label: t("reports"),
      role: [UserRole.ORGANISATION_ADMIN],
      type: NavbarLinkType.ORGANISATION_ADMIN,
      id: "navbar-link-reports",
    },
    {
      activeRegex: PAGE_ROUTE.ORGANISATION.TEAM_SESSIONS.HOME,
      href: PAGE_ROUTE.ORGANISATION.TEAM_SESSIONS.HOME,
      icon: <GroupIcon color="currentColor" />,
      label: t("teamSessions"),
      role: [UserRole.ORGANISATION_ADMIN],
      type: NavbarLinkType.ORGANISATION_ADMIN,
      id: LINK_ORGANISATION_TEAM_SESSIONS_ID,
    },
    {
      activeRegex: "/organisation/members",
      href: "/organisation/members",
      icon: <UsersIcon />,
      label: t("members"),
      role: [UserRole.ORGANISATION_ADMIN],
      type: NavbarLinkType.ORGANISATION_ADMIN,
      id: "navbar-link-members",
    },
    {
      activeRegex: "/organisation/resources",
      href: "/organisation/resources",
      icon: <ReviewIcon color="currentColor" />,
      label: t("organisationResources"),
      role: [UserRole.ORGANISATION_ADMIN],
      type: NavbarLinkType.ORGANISATION_ADMIN,
      id: "navbar-link-bm-resources",
    },
    // Administration section Links
    ...getAdminLinks(t, googleDriveIntegrationEnabled),
    buildMessagesNavBarLink(t, isProfessional),
    buildCrisisSupportNavBarLink(t, crisisSupportEnabled),
    {
      activeRegex: "^/profile(?:/.*)?$",
      href: "/profile",
      icon: <UserIcon />,
      label: user?.name ?? t("profile"),
      role: Object.values(UserRole),
      type: NavbarLinkType.SECONDARY,
      id: LINK_PROFILE_ID,
    },
  ].filter(Boolean) as NavbarLink[];

  const employeeLinks = links.filter((link) =>
    link.role.some((role) =>
      ([UserRole.EMPLOYEE, UserRole.MANAGER] as UserRole[]).includes(role),
    ),
  );
  const organisationAdminLinks = links.filter((link) =>
    link.role.some((role) =>
      ([UserRole.ORGANISATION_ADMIN] as UserRole[]).includes(role),
    ),
  );
  const professionalLinks = links.filter((link) =>
    link.role.some((role) => ([UserRole.MHP] as UserRole[]).includes(role)),
  );
  const clinicalSupervisorLinks = links.filter((link) =>
    containsSupervisorRole(link.role),
  );
  const impersonatorLinks = links.filter((link) =>
    link.role.includes(UserRole.IMPERSONATOR),
  );

  const adminLinks = links.filter((link) =>
    link.role.some((role) => ([UserRole.ADMIN] as UserRole[]).includes(role)),
  );

  return {
    clinicalSupervisorLinks,
    employeeLinks,
    organisationAdminLinks,
    professionalLinks,
    impersonatorLinks,
    adminLinks,
  };
}

function SuspendedMessageCounterDot() {
  const unreadMessagesCount = useChatUnreadCount();

  if (unreadMessagesCount <= 0) {
    return null;
  }

  return (
    <CounterDot count={unreadMessagesCount} backgroundColor="bg-red-500" />
  );
}

function MessageCounterDot() {
  return (
    <Suspense fallback={null}>
      <SuspendedMessageCounterDot />
    </Suspense>
  );
}
